import { useEffect, useState } from 'react';
import './matchup-preview.styles.scss';
import { positions } from '@/utils/positions';
import { listenToDoc } from '@/utils/getDataFromDb';
import { DEFENSES, OFFENSES, PACES } from '@shared/strategy.js';
import { isUnavailable, startingLineup } from '@shared/depthChart.js';
import { planStrategy } from '@shared/gamePlan.js';
import { paybackReason, rivalryOf } from '@shared/rivalry.js';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import PrestigeStars from '@/components/SHARED/prestige-stars/prestige-stars.component';

// The tale of the tape before a replay: both teams as they stood when the
// game tipped off (records, rankings and scoring through the day before,
// ratings of the players who dressed, the starting fives and game plans),
// so nothing gives the result away. Public information only. compact:
// during the replay, under the scoreboard, so the teams aren't repeated
// (only the game's tags are kept).
// upcoming: `game` is a summary/schedule entry for a game not played yet;
// the sides are built from the team documents as they stand now (healthy
// players, projected starters from the current depth chart, the manager's
// own plan from `plans`, and what the opponent has run most this season,
// since its plan stays secret until tip-off).
const MatchupPreview = ({ leagueId, league, game: given, teams, schedule, rankHistory, teamId, compact, upcoming, plans }) => {

    const [ docs, setDocs ] = useState({});
    const awayId = upcoming ? given.away : given.away.id;
    const homeId = upcoming ? given.home : given.home.id;

    // The rosters' ratings (they don't change during a season).
    useEffect(() => {
        setDocs({});
        const unsubs = [];
        for (const id of [awayId, homeId]) {
            listenToDoc({
                'path': `leagues/${leagueId}/teams/${id}`,
                'callback': (d) => setDocs(s => ({ ...s, [id]: d })),
                'handleListener': (u) => unsubs.push(u),
            });
        }
        return () => unsubs.forEach(u => u());
    }, [leagueId, awayId, homeId])

    const game = upcoming ? upcomingGame(given, docs, { teams, schedule, league, teamId, plans }) : given;

    const college = league.type !== 'pro';
    // Pro playoff games are kept apart from the season record.
    const separate = league.settings.postseason?.format === 'series';
    const thisSeason = (game.year ?? 1) === (league.season.year ?? 1);
    const games = thisSeason ? schedule?.games ?? [] : [];
    const side = (s) => {
        const t = game[s];
        return {
            ...t,
            summary: teams?.[t.id],
            form: formBefore(games, t.id, game.day, separate),
            rank: thisSeason ? rankBefore(rankHistory, t.id, game.day) : null,
            ratings: lineupRatings(t, docs[t.id]),
        };
    };
    const away = side('away');
    const home = side('home');
    const meetings = games
        .filter(g => g.result && g.day < game.day && g.id !== game.id &&
            ((g.home === home.id && g.away === away.id) || (g.home === away.id && g.away === home.id)))
        .sort((a, b) => a.day - b.day);

    const rec = (f) => (f.w + f.l ? `${f.w}-${f.l}` : '0-0');
    const per = (n, gp) => (gp ? (n / gp).toFixed(1) : '-');
    const pctOf = (f) => (f.w + f.l ? f.w / (f.w + f.l) : null);
    const rankLimit = college ? 25 : Infinity;
    const rankText = (r) => (r && r <= rankLimit ? `#${r}` : college ? 'Unranked' : '-');
    const label = (list, key) => list.find(x => x.key === key)?.label ?? '-';

    // [label, away text, home text, away value, home value, higher wins?]
    const rows = [
        ['Record', rec(away.form), rec(home.form), pctOf(away.form), pctOf(home.form), true],
        game.conf && ['Conference', `${away.form.cw}-${away.form.cl}`, `${home.form.cw}-${home.form.cl}`, null, null],
        ['Last 5', away.form.last.join(' ') || '-', home.form.last.join(' ') || '-', null, null],
        ['Streak', streak(away.form.last), streak(home.form.last), null, null],
        (away.rank || home.rank) && [college ? 'Top 25' : 'Power ranking', rankText(away.rank), rankText(home.rank),
            away.rank && away.rank <= rankLimit ? -away.rank : null, home.rank && home.rank <= rankLimit ? -home.rank : null, true],
        college && ['Prestige', <PrestigeStars key='a' value={away.summary?.prestige} label='Program prestige' />,
            <PrestigeStars key='h' value={home.summary?.prestige} label='Program prestige' />, away.summary?.prestige, home.summary?.prestige, true],
        ['Chemistry', away.summary?.chemistryGrade?.grade ?? '-', home.summary?.chemistryGrade?.grade ?? '-',
            gradeValue(away.summary?.chemistryGrade?.grade), gradeValue(home.summary?.chemistryGrade?.grade), true],
        ['Points', per(away.form.pf, away.form.gp), per(home.form.pf, home.form.gp),
            away.form.gp && away.form.pf / away.form.gp, home.form.gp && home.form.pf / home.form.gp, true],
        ['Allowed', per(away.form.pa, away.form.gp), per(home.form.pa, home.form.gp),
            away.form.gp && away.form.pa / away.form.gp, home.form.gp && home.form.pa / home.form.gp, false],
        ['Overall', away.ratings?.overall ?? '-', home.ratings?.overall ?? '-', away.ratings?.overall, home.ratings?.overall, true],
        ['Offense', away.ratings?.off ?? '-', home.ratings?.off ?? '-', away.ratings?.off, home.ratings?.off, true],
        ['Defense', away.ratings?.def ?? '-', home.ratings?.def ?? '-', away.ratings?.def, home.ratings?.def, true],
        ['Starters', away.ratings?.starters ?? '-', home.ratings?.starters ?? '-', away.ratings?.starters, home.ratings?.starters, true],
        ['Bench', away.ratings?.bench ?? '-', home.ratings?.bench ?? '-', away.ratings?.bench, home.ratings?.bench, true],
        ['Stamina', away.ratings?.stamina ?? '-', home.ratings?.stamina ?? '-', away.ratings?.stamina, home.ratings?.stamina, true],
    ].filter(Boolean);

    const planRows = [
        ['Offense', label(OFFENSES, away.strategy?.offense), label(OFFENSES, home.strategy?.offense)],
        ['Defense', label(DEFENSES, away.strategy?.defense), label(DEFENSES, home.strategy?.defense)],
        ['Pace', label(PACES, away.strategy?.pace), label(PACES, home.strategy?.pace)],
        (away.strategy?.keyOn || home.strategy?.keyOn) &&
            ['Key on', keyName(away.strategy?.keyOn, game.home), keyName(home.strategy?.keyOn, game.away)],
    ].filter(Boolean);
    const hasPlans = away.strategy || home.strategy;
    // Upcoming: the viewer's own side is their saved lineup and plan; any
    // other side's is projected.
    const iPlay = away.id === teamId || home.id === teamId;
    const ownPlan = Boolean(plans?.games?.[game.id]);
    const planTag = (t) => (!upcoming ? null : t.id === teamId ?
        <span className='mp-plan-tag saved' title={ownPlan ? 'Your saved plan for this game' : 'Your usual strategy (no plan saved for this game)'}>✓ Saved</span> :
        <span className='mp-plan-tag'>Projected</span>);

    const hasTags = game.label || away.payback || home.payback;

    const better = (a, h, high) => {
        if (high === undefined || a == null || h == null || a === h) return [false, false];
        return high ? [a > h, h > a] : [a < h, h < a];
    };

    return (
        <div className='matchup-preview'>
            {!compact &&
                <header className='mp-preview-head'>
                    <span className='mp-preview-kicker'>{upcoming ? `Day ${game.day}` : 'Pregame'}</span>
                    <h2>Matchup preview</h2>
                    <span className='mp-preview-note'>
                        {upcoming ?
                            'As things stand now. Injured players are left out, and the other team’s game plan stays secret until tip-off.' :
                            'Before tip-off. The score stays hidden until you watch or skip to the final.'}
                    </span>
                </header>
            }
            {game.rivalry &&
                <div className='mp-rivalry-banner' style={{ '--away': away.color1 ?? '#555555', '--home': home.color1 ?? '#222222' }}>
                    <div className='mp-rivalry-plate'>
                        <span className='mp-rivalry-kicker'>{game.rivalry.name ? 'Rivalry game' : `${away.abbr} vs ${home.abbr}`}</span>
                        <span className='mp-rivalry-title'>{game.rivalry.name ?? 'Rivalry game'}</span>
                        <span className='mp-rivalry-note'>Throw out the records</span>
                    </div>
                </div>
            }
            {(!compact || hasTags) && <div className='g-card mp-head'>
                {hasTags &&
                    <div className='mp-tags'>
                        {game.label && <span className='mp-label'>{game.label}</span>}
                        {[away, home].filter(s => s.payback).map(s => (
                            <span key={s.id} className='mp-payback'>Payback game for {s.abbr ?? s.name}</span>
                        ))}
                    </div>
                }
                {!compact && <div className='mp-teams'>
                    {[away, null, home].map((t, i) => (t ?
                        <div key={t.id} className={`mp-team ${t.id === teamId ? 'mine' : ''}`}>
                            <TeamBadge team={t} size='lg' />
                            <span className='mp-name'>{t.name}</span>
                            <span className='meta'>{t.mascot}</span>
                            <span className='mp-record'>{rec(t.form)}</span>
                            {t.summary?.managerName && <span className='meta'>{t.summary.managerName}</span>}
                        </div> :
                        <div key={i} className='mp-vs'>
                            <b>{game.neutral ? 'vs' : 'at'}</b>
                            <span className='meta'>{game.neutral ? 'Neutral site' : `${home.abbr} home`}</span>
                        </div>
                    ))}
                </div>}
            </div>}

            <div className='g-card'>
                <h3>Tale of the tape</h3>
                <table className='mp-compare'>
                    <thead>
                        <tr><th>{away.abbr}</th><th aria-hidden='true'></th><th>{home.abbr}</th></tr>
                    </thead>
                    <tbody>
                        {rows.map(([name, a, h, av, hv, high]) => {
                            const [aBest, hBest] = better(av, hv, high);
                            return (
                                <tr key={name}>
                                    <td className={aBest ? 'best' : ''}>{a}</td>
                                    <th scope='row'>{name}</th>
                                    <td className={hBest ? 'best' : ''}>{h}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                <p className='meta'>
                    {upcoming ?
                        'Records and scoring so far this season. Ratings average the top eight healthy players; starters are projected from each team’s current lineup, bench is the next three by overall.' :
                        'Records and scoring are through the day before this game. Ratings average the top eight players who dressed; starters and bench (the next three) by overall.'}
                </p>
                {meetings.length > 0 &&
                    <div className='mp-meetings'>
                        <h4>Earlier this season</h4>
                        <ul>
                            {meetings.map(g => {
                                const awayPts = g.home === away.id ? g.result.home : g.result.away;
                                const homePts = g.home === home.id ? g.result.home : g.result.away;
                                return <li key={g.id}>{away.abbr} {awayPts}, {home.abbr} {homePts}{g.result.ot ? ' (OT)' : ''}</li>;
                            })}
                        </ul>
                    </div>
                }
            </div>

            <div className='g-card'>
                <h3>{!upcoming ? 'Starting lineups' : iPlay ? 'Game plans' : 'Projected game plans'}</h3>
                <div className='mp-lineups'>
                    {[away, home].map(t => (
                        <div key={t.id} className='mp-lineup'>
                            <h4><TeamBadge team={t} size='sm' /> {t.abbr} {planTag(t)}</h4>
                            <ol>
                                {starters(t, docs[t.id]).map(p => (
                                    <li key={p.id}>
                                        <span className='mp-pos'>{positions[p.pos1 - 1]?.short}</span>
                                        <span className='mp-player'>{p.fName[0]}. {p.lName}</span>
                                        <span className='mp-ovr'>{p.overall ?? ''}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    ))}
                </div>
                {hasPlans &&
                    <>
                        <h4 className='mp-plan-title'>{upcoming ? 'Strategy' : 'Game plans'}</h4>
                        <table className='mp-compare'>
                            {upcoming &&
                                <thead>
                                    <tr><th>{away.abbr} {planTag(away)}</th><th aria-hidden='true'></th><th>{home.abbr} {planTag(home)}</th></tr>
                                </thead>
                            }
                            <tbody>
                                {planRows.map(([name, a, h]) => (
                                    <tr key={name}><td>{a}</td><th scope='row'>{name}</th><td>{h}</td></tr>
                                ))}
                            </tbody>
                        </table>
                        {upcoming &&
                            <p className='meta'>
                                {iPlay && (ownPlan ?
                                    'Saved: your lineup and your plan for this game. ' :
                                    'Saved: your lineup and your usual strategy (no plan saved for this game yet). ')}
                                Projected: {iPlay ? 'the other team’s' : 'each team’s'} current lineup and what {iPlay ? 'it has' : 'they have'} run most this season. The real plans stay secret until tip-off.
                            </p>
                        }
                    </>
                }
            </div>
        </div>
    )
}

// A game-document-shaped object for a game not played yet, from its
// schedule entry and the two team documents (visible fields only).
function upcomingGame(entry, docs, { teams, schedule, league, teamId, plans }) {
    const year = league.season.year ?? 1;
    const side = (id, oppId) => {
        const doc = docs[id];
        const sum = teams?.[id] ?? {};
        const all = doc?.players ?? {};
        const roster = Object.fromEntries(Object.entries(all).filter(([, p]) => !isUnavailable(p)));
        const start = new Set(doc ? startingLineup(all, doc.depth) : []);
        return {
            id,
            name: doc?.name ?? sum.name,
            abbr: doc?.abbr ?? sum.abbr,
            mascot: doc?.mascot ?? sum.mascot,
            color1: doc?.color1 ?? sum.color1,
            color2: doc?.color2 ?? sum.color2,
            roster,
            players: Object.fromEntries(Object.keys(roster).map(pid => [pid, { starter: start.has(pid) }])),
            strategy: !doc ? null : id === teamId && plans !== undefined ?
                planStrategy(plans, entry.id, doc.strategy) : mostRun(doc, year),
            payback: Boolean(paybackReason({ teamId: id, oppId, games: schedule?.games, day: entry.day, payback: doc?.payback ?? sum.payback, year })),
        };
    };
    const rivalry = rivalryOf(docs[entry.home] ?? teams?.[entry.home], entry.away);
    return {
        ...entry,
        year,
        away: side(entry.away, entry.home),
        home: side(entry.home, entry.away),
        rivalry: rivalry ? { name: rivalry.name } : null,
    };
}

// What a team has run most this season (its public scouting log), else
// what it ran in its last game. No key-on: that's decided game by game.
function mostRun(doc, year) {
    const log = doc.scoutLog?.year === year ? doc.scoutLog.ran : null;
    const top = (cat) => {
        const counts = Object.entries(log?.[cat] ?? {});
        if (!counts.length) return doc.strategy?.[cat];
        return counts.sort((a, b) => b[1] - a[1])[0][0];
    };
    return { offense: top('offense'), defense: top('defense'), pace: top('pace') };
}

// A team's season through the day before `day`: record, conference
// record, last five results (oldest first), points for and against.
function formBefore(games, teamId, day, separate) {
    const f = { w: 0, l: 0, cw: 0, cl: 0, pf: 0, pa: 0, gp: 0, last: [] };
    const played = games
        .filter(g => g.result && g.day < day && (g.home === teamId || g.away === teamId) && !(separate && g.post))
        .sort((a, b) => a.day - b.day);
    for (const g of played) {
        const mine = g.home === teamId ? g.result.home : g.result.away;
        const theirs = g.home === teamId ? g.result.away : g.result.home;
        const won = mine > theirs;
        f[won ? 'w' : 'l'] += 1;
        if (g.conf && !g.post) f[won ? 'cw' : 'cl'] += 1;
        f.pf += mine;
        f.pa += theirs;
        f.gp += 1;
        f.last.push(won ? 'W' : 'L');
    }
    f.last = f.last.slice(-5);
    return f;
}

// "W3" / "L2" from the latest results.
// The player a defense keyed on (on the other team's game roster), or "-".
function keyName(playerId, other) {
    if (!playerId) return '-';
    const p = other.roster?.[playerId];
    return p ? `${p.fName[0]}. ${p.lName}` : '-';
}

function streak(last) {
    if (!last.length) return '-';
    const end = last.at(-1);
    let n = 0;
    for (let i = last.length - 1; i >= 0 && last[i] === end; i--) n++;
    return `${end}${n}`;
}

// The team's ranking after the last game day before `day`.
function rankBefore(history, teamId, day) {
    const ranks = history?.teams?.[teamId];
    const days = history?.days;
    if (!ranks || !days) return null;
    let at = -1;
    for (let i = 0; i < days.length; i++) if (days[i] < day) at = i;
    return at >= 0 ? ranks[at] ?? null : null;
}

// Visible ratings of the players who dressed for the game.
function lineupRatings(side, teamDoc) {
    const ps = dressed(side, teamDoc);
    if (!ps.length) return null;
    const avg = (list, k) => (list.length ? Math.round(list.reduce((s, p) => s + (p[k] ?? 0), 0) / list.length) : null);
    const top = [...ps].sort((a, b) => b.overall - a.overall).slice(0, 8);
    const start = ps.filter(p => side.players?.[p.id]?.starter);
    const bench = ps.filter(p => !side.players?.[p.id]?.starter).sort((a, b) => b.overall - a.overall).slice(0, 3);
    return {
        overall: avg(top, 'overall'),
        off: avg(top, 'off_quality'),
        def: avg(top, 'def_quality'),
        stamina: avg(top, 'stamina'),
        starters: avg(start, 'overall'),
        bench: avg(bench, 'overall'),
    };
}

function dressed(side, teamDoc) {
    return Object.keys(side.roster ?? {}).map(id => teamDoc?.players?.[id]).filter(Boolean);
}

// The five who started, by position; names from the game when the team
// document doesn't have them any more (a trade since).
function starters(side, teamDoc) {
    return Object.keys(side.roster ?? {})
        .filter(id => side.players?.[id]?.starter)
        .map(id => ({ id, ...side.roster[id], overall: teamDoc?.players?.[id]?.overall }))
        .sort((a, b) => a.pos1 - b.pos1);
}

function gradeValue(grade) {
    if (!grade) return null;
    const base = { A: 4, B: 3, C: 2, D: 1, F: 0 }[grade[0]];
    if (base === undefined) return null;
    return base + (grade[1] === '+' ? 0.3 : grade[1] === '-' ? -0.3 : 0);
}

export default MatchupPreview
